import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DataItem } from 'scrivito'

import { Button } from '@/Atomic/Molecules/Button'
import DropdownMenu from '@/Atomic/Organisms/DropdownMenu'
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@/Atomic/Templates/Drawer'
import { Form } from '@/Atomic/Templates/Form'
import { DataItemFormBody } from '@/Atomic/Widgets/DataItemForm/DataItemForm.Body'
import { DeleteAddressModal } from '@/Atomic/Widgets/DataItemTabs/SharedTabs/AddressTab/Modal/DeleteAddressModal'
import { DataClassName } from '@/Data/const'
import { useModalHandler } from '@/hooks/useModalHandler'
import { useDataItemFormHandler } from '@/utils/CRUD/hooks/useDataItemFormHandler'
import { getSchemaName } from '@/utils/dataClassMap'
import { getDisplayValue } from '@/utils/getDisplayValue'
import { getSchema } from '@/utils/getSchema'

import './DataItemDrawer.scss'

interface DataItemAddressDrawerProps {
  dataItem: DataItem
  isOpen: boolean
  close: () => void
  openDrawerId?: (id?: string) => void
  nextId?: string
  previousId?: string
}

export const DataItemAddressDrawer: React.FC<DataItemAddressDrawerProps> = ({
  dataItem,
  isOpen,
  close,
  openDrawerId,
  nextId,
  previousId,
}) => {
  const { t } = useTranslation()

  const {
    methods,
    onFormSubmit,
    handleSave,
    isEditing,
    abortEditing,
    toggleEdit,
  } = useDataItemFormHandler(dataItem)

  const { isOpen: modalIsOpen, closeModal, openModal } = useModalHandler()

  const { schema: addressSchema } = getSchema(
    getSchemaName(DataClassName.ADDRESS),
  )
  const addressTypeTitle = addressSchema
    ? getDisplayValue({
        rawValue: dataItem.get('type'),
        key: 'type',
        schema: addressSchema,
      })
    : t('components.address.address')

  return (
    <Drawer isOpen={isOpen} close={close} direction="right">
      <DrawerHeader>
        <div className="header item">
          <Button
            variant="icon"
            icon="cross"
            tooltip={t('tooltip.close')}
            onClick={close}
          />
          <Button
            variant="icon"
            icon="chevron-up"
            tooltip={t('tooltip.previous')}
            onClick={openDrawerId ? () => openDrawerId(previousId) : undefined}
            disabled={!previousId}
            hidden={!openDrawerId}
          />
          <Button
            variant="icon"
            icon="chevron-down"
            tooltip={t('tooltip.next')}
            onClick={openDrawerId ? () => openDrawerId(nextId) : undefined}
            disabled={!nextId}
            hidden={!openDrawerId}
          />
        </div>
        <div className="header item">
          <Button
            variant="icon"
            icon="edit"
            tooltip={t('tooltip.edit')}
            onClick={toggleEdit}
          />
          <DropdownMenu
            buttonClassName="borderless button"
            menuItems={[
              {
                icon: 'bin',
                label: t('common.delete'),
                onClick: openModal,
                type: 'danger',
              },
            ]}
          />
          <DeleteAddressModal
            isOpen={modalIsOpen}
            close={closeModal}
            confirm={() => dataItem.delete().then(() => close())}
            addressType={addressTypeTitle}
          />
        </div>
      </DrawerHeader>
      <DrawerBody>
        <FormProvider {...methods}>
          <Form onSubmit={onFormSubmit}>
            <DataItemFormBody dataItem={dataItem} isEditing={isEditing} />
          </Form>
        </FormProvider>
      </DrawerBody>
      {isEditing && (
        <DrawerFooter align="right">
          <Button variant="outlined" onClick={abortEditing} size="lg">
            {t('common.cancel')}
          </Button>
          <Button icon="disk" onClick={handleSave} size="lg">
            {t('common.saveChanges')}
          </Button>
        </DrawerFooter>
      )}
    </Drawer>
  )
}
